import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';

import i18n from '../../i18n';
import { getTypeColor, PDF_COLORS } from './pdfColors';
import { CoverPage } from './components/CoverPage';
import { DataGrid } from './components/DataGrid';
import { ImageGrid } from './components/ImageGrid';
import { PDFTable } from './components/PDFTable';
import { RecordSummary } from './DetailedReport';
import { SurveillanceReportData } from './surveillanceTypes';

// ═══════════════════════════════════════════════════════════════════
// Informe del Recorregut de Vigilància (2026-07-27).
//
// Documento PROPIO, no el combinado genérico, por dos razones:
//   (a) la tabla de tramos necesita página HORIZONTAL — 12 columnas no caben
//       legibles en A4 vertical;
//   (b) el combinado cuenta accidentes y comunicaciones, que en un RV son
//       siempre 0 (matriz D0: solo admite Actuacions y tramos).
//
// Qué se pinta lo decide `data.options`, elegido en el modal. La ficha y la
// portada van siempre; el resto son bloques opcionales.
// ═══════════════════════════════════════════════════════════════════

function t(key: string, defaultValue?: string): string {
  return i18n.t(key, { defaultValue: defaultValue ?? key });
}

Font.register({
  family: 'Poppins',
  fonts: [
    { src: '/fonts/Poppins-Regular.ttf' },
    { src: '/fonts/Poppins-SemiBold.ttf', fontWeight: 600 },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLORS.white,
    paddingTop: 20,
    paddingBottom: 35,
    paddingHorizontal: 20,
    fontFamily: 'Poppins',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 10,
  },
  roidText: {
    fontSize: 11,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    letterSpacing: 0.3,
  },
  stateBadge: {
    paddingVertical: 2,
    paddingHorizontal: 7,
    borderRadius: 8,
    backgroundColor: 'rgba(255,255,255,0.22)',
  },
  stateText: {
    fontSize: 7.5,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  sectionTitle: {
    fontSize: 8.5,
    fontFamily: 'Poppins',
    fontWeight: 600,
    marginTop: 9,
    marginBottom: 4,
    paddingBottom: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: PDF_COLORS.greyMid,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  descriptionBlock: {
    marginTop: 6,
    marginBottom: 4,
    padding: 6,
    backgroundColor: PDF_COLORS.greyLight,
    borderRadius: 3,
  },
  descriptionLabel: {
    fontSize: 7,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.greyDark,
    marginBottom: 2,
  },
  descriptionText: {
    fontSize: 8,
    fontFamily: 'Poppins',
    color: PDF_COLORS.ink,
    lineHeight: 1.45,
  },
  emptyText: {
    fontSize: 8,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
    marginTop: 4,
  },
  photoGroupTitle: {
    fontSize: 8,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
    marginTop: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: PDF_COLORS.greyMid,
    paddingTop: 3,
  },
  footerText: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
  },
  footerBrand: {
    flex: 1,
    textAlign: 'center',
  },
});

/**
 * Pie de página fijo, réplica del de `HistoricalReport`/`DetailedReport`. Se
 * define aquí (en vez de importarlo) para no tocar esos ficheros: hay un
 * re-merge pendiente de la PR de Gestión Horaria que los modifica.
 */
const ReportFooter = ({ title }: { title: string }) => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>{title}</Text>
    <Text style={[styles.footerText, styles.footerBrand]}>
      UTE Eix Transversal C-25 · Generalitat de Catalunya
    </Text>
    {/* Sin «página X / Y»: retirado a petición del cliente (2026-08-04). El
        hueco se conserva para que la marca siga centrada en el pie. */}
    <Text style={styles.footerText} />
  </View>
);

/** `ImageGrid` pinta como mucho 6 imágenes; el anexo las trocea para no perder ninguna. */
const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

/** Cabeceras y anchos de la tabla de tramos según las opciones. */
function buildSegmentsTable(data: SurveillanceReportData) {
  const extra = data.options.segmentExtraColumns;
  // Multi-carretera (2026-08-15): la columna «Carretera» solo aparece cuando el
  // recorrido tiene más de una, que es cuando el PK deja de decir dónde por sí
  // solo. `buildSegmentRows` ya toma esa decisión y solo entonces rellena
  // `road`, así que aquí basta con mirar si viene.
  const showRoad = data.segments.some((s) => s.road !== undefined);

  const headers = [
    // Literal, igual que la cabecera de la tabla de pantalla (es un símbolo de
    // numeración, no copy traducible).
    'Nº',
    ...(showRoad ? [t('pages:surveillance.road')] : []),
    t('forms:workOrder.direction'),
    ...(extra ? [t('common:tables.headers.roid')] : []),
    t('common:tables.headers.pkInit'),
    t('common:tables.headers.pkEnd'),
    // Unidad física, igual que en la tabla de pantalla.
    'Km',
    t('pages:surveillance.startTime'),
    t('pages:surveillance.endTime'),
    t('forms:timeManagement.headers.duration'),
    ...(extra ? [t('forms:workOrder.lane')] : []),
    t('pages:surveillance.pause.cause'),
    t('common:tables.headers.description'),
    // 30-oct: la columna también lleva la actuación de la OT del aviso.
    t('pages:surveillance.actionColumn'),
    t('pages:surveillance.linkedExternalColumn'),
  ];

  const colFlex = [
    0.45, // Nº
    ...(showRoad ? [1.0] : []), // Carretera
    1.2, // Sentido
    ...(extra ? [1.5] : []), // ROID del tramo
    0.9, // PK inicial
    0.9, // PK final
    0.55, // Km
    0.7, // Hora inicio
    0.7, // Hora final
    0.8, // Duración
    ...(extra ? [0.8] : []), // Carril
    1.2, // Causa
    2.2, // Descripción — la única que debe poder crecer
    1.3, // Act. básica
    1.3, // OT sala
  ];

  const align: ('left' | 'right' | 'center')[] = [
    'center',
    ...(showRoad ? ['left' as const] : []),
    'left',
    ...(extra ? ['left' as const] : []),
    'left',
    'left',
    'right',
    'left',
    'left',
    'right',
    ...(extra ? ['left' as const] : []),
    'left',
    'left',
    'left',
    'left',
  ];

  const rows = data.segments.map((segment) => [
    String(segment.index),
    ...(showRoad ? [segment.road ?? ''] : []),
    segment.direction,
    ...(extra ? [segment.roid] : []),
    segment.pkInit,
    segment.pkEnd,
    segment.km,
    segment.startTime,
    segment.endTime,
    segment.duration,
    ...(extra ? [segment.lane] : []),
    segment.pauseCause,
    segment.description,
    segment.linkedBasicActionRoid,
    segment.linkedExternalRoid,
  ]);

  return { headers, rows, colFlex, align };
}

export const SurveillanceReport = ({
  data,
}: {
  data: SurveillanceReportData;
}) => {
  const color = getTypeColor('SurveillanceRoute');
  const actionColor = getTypeColor('Action');
  const showChildrenList = data.childRows.length > 0;

  return (
    <Document>
      <CoverPage
        title={data.title}
        typeColor={color}
        generatedAt={data.generatedAt}
        totalCount={data.counts.segments}
        filtersSummary={[
          {
            label: t('modals:surveillanceReport.segmentsTitle'),
            value: String(data.counts.segments),
          },
          ...(data.options.children !== 'none'
            ? [
                {
                  label: t('navigation:routes.actions'),
                  value: String(data.counts.actions),
                },
              ]
            : []),
          ...(data.options.linkedOrders !== 'none'
            ? [
                {
                  label: t('modals:surveillanceReport.linkedOrdersTitle'),
                  value: String(data.counts.linkedOrders),
                },
              ]
            : []),
        ]}
      />

      {/* ── Ficha del recorrido (siempre) ── */}
      <Page size="A4" style={styles.page} wrap={true}>
        <View style={[styles.header, { backgroundColor: color }]} wrap={false}>
          <Text style={styles.roidText}>{data.roid}</Text>
          {data.state ? (
            <View style={styles.stateBadge}>
              <Text style={styles.stateText}>
                {t(`common:states.${data.state}`, data.state)}
              </Text>
            </View>
          ) : null}
        </View>

        <Text style={styles.sectionTitle}>
          {t('modals:surveillanceReport.generalData')}
        </Text>
        <DataGrid fields={data.basicInfo} />

        {data.description ? (
          <View style={styles.descriptionBlock} wrap={false}>
            <Text style={styles.descriptionLabel}>
              {t('common:tables.headers.description')}
            </Text>
            <Text style={styles.descriptionText}>{data.description}</Text>
          </View>
        ) : null}

        {data.notes ? (
          <View style={styles.descriptionBlock} wrap={false}>
            <Text style={styles.descriptionLabel}>{t('forms:workOrder.notes')}</Text>
            <Text style={styles.descriptionText}>{data.notes}</Text>
          </View>
        ) : null}

        {data.crew.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>
              {t('modals:surveillanceReport.crewTitle')}
            </Text>
            <PDFTable
              table={{
                headers: [
                  t('tables:headers.operator'),
                  t('common:tables.headers.plate'),
                  t('common:tables.headers.driver'),
                ],
                rows: data.crew.map((member) => [
                  member.operator,
                  member.vehicle,
                  member.driver,
                ]),
                colFlex: [2, 1, 2],
              }}
            />
          </>
        )}

        {data.images.length > 0 && <ImageGrid images={data.images} />}

        <ReportFooter title={data.title} />
      </Page>

      {/* ── Tramos: página HORIZONTAL (12+ columnas) ── */}
      {data.options.segmentsTable && (
        <Page
          size="A4"
          orientation="landscape"
          style={styles.page}
          wrap={true}
        >
          <Text style={styles.sectionTitle}>
            {`${t('modals:surveillanceReport.segmentsTitle')} (${data.counts.segments})`}
          </Text>

          {data.segments.length === 0 ? (
            <Text style={styles.emptyText}>
              {t('modals:surveillanceReport.noSegments')}
            </Text>
          ) : (
            // 40 tramos ocupan 3 páginas: sin esto, de la segunda en adelante
            // salían filas sueltas sin saber qué columna es cuál. Esta página
            // solo lleva la tabla (y el resumen por causa, debajo del final),
            // así que `fixed` no se cuela sobre otro contenido.
            <PDFTable table={buildSegmentsTable(data)} repeatHeader />
          )}

          {data.causeSummary.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>
                {t('modals:surveillanceReport.causeSummaryTitle')}
              </Text>
              <PDFTable
                table={{
                  headers: [
                    t('pages:surveillance.pause.cause'),
                    t('modals:surveillanceReport.segmentsTitle'),
                    t('forms:timeManagement.headers.duration'),
                    'Km',
                  ],
                  rows: data.causeSummary.map((row) => [
                    row.cause,
                    String(row.count),
                    row.duration,
                    row.km,
                  ]),
                  colFlex: [2, 1, 1, 1],
                  align: ['left', 'right', 'right', 'right'],
                }}
              />
            </>
          )}

          <ReportFooter title={data.title} />
        </Page>
      )}

      {/* ── Fotos de los tramos (anexo, vertical, un bloque por tramo) ── */}
      {data.segmentPhotos.map((group, groupIndex) => (
        <Page
          key={`segment-photos-${groupIndex}`}
          size="A4"
          style={styles.page}
          wrap={true}
        >
          <Text style={styles.sectionTitle}>
            {t('modals:surveillanceReport.segmentPhotosTitle')}
          </Text>
          <Text style={styles.photoGroupTitle}>{group.title}</Text>
          {chunk(group.images, 6).map((images, blockIndex) => (
            <ImageGrid key={blockIndex} images={images} />
          ))}
          <ReportFooter title={data.title} />
        </Page>
      ))}

      {/* ── Actuaciones asociadas: listado ── */}
      {showChildrenList && (
        <Page size="A4" style={styles.page} wrap={true}>
          <Text style={styles.sectionTitle}>
            {`${t('modals:surveillanceReport.childrenTitle')} (${data.childRows.length})`}
          </Text>
          <PDFTable
            table={{
              headers: [
                t('common:tables.headers.roid'),
                t('tables:headers.date'),
                t('modals:surveillanceReport.segmentsTitle'),
                t('common:tables.headers.state'),
                t('common:tables.headers.description'),
              ],
              rows: data.childRows.map((row) => [
                row.roid,
                row.time,
                row.segment,
                row.state,
                row.description,
              ]),
              colFlex: [1.6, 1.4, 0.6, 1.1, 3],
              align: ['left', 'left', 'center', 'left', 'left'],
            }}
            // Misma razón que en tramos: esta página es solo el listado.
            repeatHeader
          />
          <ReportFooter title={data.title} />
        </Page>
      )}

      {/* ── Actuaciones asociadas: anexo de fotos ── */}
      {data.childPhotos.map((group, groupIndex) => (
        <Page
          key={`photos-${groupIndex}`}
          size="A4"
          style={styles.page}
          wrap={true}
        >
          <Text style={styles.sectionTitle}>
            {t('modals:surveillanceReport.photosTitle')}
          </Text>
          <Text style={styles.photoGroupTitle}>{group.title}</Text>
          {chunk(group.images, 6).map((images, blockIndex) => (
            <ImageGrid key={blockIndex} images={images} />
          ))}
          <ReportFooter title={data.title} />
        </Page>
      ))}

      {/* ── Actuaciones asociadas: detalle completo (mismo render que el resto de informes) ── */}
      {data.childRecords.map((record, index) => (
        <RecordSummary
          key={`child-${index}`}
          record={record}
          typeColor={actionColor}
          title={data.title}
        />
      ))}

      {/* ── Órdenes vinculadas al recorrido (cross-link 3): ficha, primera
          comunicación y trabajos del equipo del recorrido en el turno ── */}
      {data.linkedOrders.map((block, blockIndex) => (
        <React.Fragment key={`linked-${blockIndex}`}>
          <Page size="A4" style={styles.page} wrap={true}>
            <Text style={styles.sectionTitle}>{block.title}</Text>
            <DataGrid fields={block.info} />
            <Text style={styles.sectionTitle}>
              {`${t('modals:surveillanceReport.linkedWorksTitle')} (${
                block.workRows.length || block.workRecords.length
              })`}
            </Text>
            {block.workRows.length > 0 ? (
              <PDFTable
                table={{
                  headers: [
                    t('common:tables.headers.roid'),
                    t('tables:headers.type'),
                    t('tables:headers.date'),
                    t('common:tables.headers.state'),
                    t('modals:surveillanceReport.registeredBy'),
                    t('common:tables.headers.description'),
                  ],
                  rows: block.workRows.map((row) => [
                    row.roid,
                    row.type,
                    row.time,
                    row.state,
                    row.registeredBy,
                    row.description,
                  ]),
                  colFlex: [1.5, 1, 1.3, 1, 1.4, 2.6],
                  align: ['left', 'left', 'left', 'left', 'left', 'left'],
                }}
                repeatHeader
              />
            ) : block.workRecords.length === 0 ? (
              <Text style={styles.photoGroupTitle}>
                {t('modals:surveillanceReport.noLinkedWorks')}
              </Text>
            ) : null}
            {/* Spec 2026-09-23 § 7: el informe es de la patrulla, pero no
                esconde que otros equipos u otros turnos trabajaron la OT. */}
            {block.otherWorksCount > 0 && (
              <Text style={styles.photoGroupTitle}>
                {i18n.t('modals:surveillanceReport.otherWorks', {
                  count: block.otherWorksCount,
                })}
              </Text>
            )}
            <ReportFooter title={data.title} />
          </Page>
          {block.firstCommunication && (
            <RecordSummary
              record={{
                ...block.firstCommunication,
                roid: `${t('modals:surveillanceReport.firstCommunicationTitle')} · ${block.firstCommunication.roid}`,
              }}
              typeColor={getTypeColor('Communication')}
              title={data.title}
            />
          )}
          {block.workPhotos.map((group, groupIndex) => (
            <Page
              key={`linked-photos-${blockIndex}-${groupIndex}`}
              size="A4"
              style={styles.page}
              wrap={true}
            >
              <Text style={styles.sectionTitle}>
                {t('modals:surveillanceReport.photosTitle')}
              </Text>
              <Text style={styles.photoGroupTitle}>{group.title}</Text>
              {chunk(group.images, 6).map((images, imgIndex) => (
                <ImageGrid key={imgIndex} images={images} />
              ))}
              <ReportFooter title={data.title} />
            </Page>
          ))}
          {block.workRecords.map((record, recordIndex) => (
            <RecordSummary
              key={`linked-record-${blockIndex}-${recordIndex}`}
              record={record}
              typeColor={getTypeColor(record.type === 'accident' ? 'Accident' : 'Action')}
              title={data.title}
            />
          ))}
        </React.Fragment>
      ))}
    </Document>
  );
};
